import { nextTick, ref, watch } from "vue";

import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import useHasuraStore from "@/stores/hasura/hasura.store";

/**
 * Whether the current Hasura JWT belongs to the current Frontegg tenant. The JWT is minted
 * asynchronously after Frontegg flips user.tenantId (see hasura.store), so on an org switch there
 * is a window where the tenant is already the new one but Apollo still holds the PREVIOUS org's
 * token — anything that fires a query or subscription in that window is authorized as the old
 * tenant (RTLCA-5588). Gate on this ref to stay paused through the window. Same discipline as
 * useRecentProjects' inline implementation.
 */
export const useHasuraTokenSettled = () => {
  const authState = useAuthStateStore();
  const hasuraStore = useHasuraStore();

  // Seed from the current token so the very first mount (token already present) is not paused.
  const isTokenSettledForTenant = ref(!!hasuraStore.hasuraToken);

  // Tenant changed → the current token is now the old tenant's; pause until the new one lands.
  watch(
    () => authState.user?.tenantId,
    () => {
      isTokenSettledForTenant.value = false;
    }
  );

  // Token settled (login, tenant switch, re-auth) → resume. nextTick lets hasura.store's own
  // watcher push the token into Apollo's defaultContext (and re-authenticate the WS socket)
  // before consumers unpause — both watch the same ref, and the store's registers first.
  watch(
    () => hasuraStore.hasuraToken,
    async () => {
      await nextTick();
      isTokenSettledForTenant.value = true;
    }
  );

  return { isTokenSettledForTenant };
};
